import { CHOIX_DE_PRIORITE } from '@nextilt/commun';
import type { BulleEnrichie, Priorite } from '../partage/api/types';
import { selonMonOrdre } from './monOrdre';
import {
  GLOSE_DE_PRIORITE,
  GLOSE_HORS_JEU,
  GLOSE_NON_REGLEE,
  HORS_JEU,
  IMPORTANCE_NON_REGLEE,
  MOTS_PRIORITE,
} from '../partage/vocabulaire';

/**
 * Les intertitres de la Liste d'une feuille (ticket 04 du chantier `priorites`, refait au
 * ticket 05 du chantier `flottaison`) : l'importance réglée, la sienne ou celle d'une mère,
 * telle que le serveur la présente (`priorite.retenue`). Sans rendu, testée dans
 * `tests/client/projets/rangerLaListe.test.ts` (« intertitresDeLaListe »).
 *
 * Dans l'ordre : Urgent, Important, Quand tu veux, « Importance non réglée » (une bulle encore
 * à faire sur laquelle personne n'a rien réglé, ni elle ni une mère), puis ce qui n'est plus à
 * faire (acquis, en réflexion), qui n'a jamais d'importance. Seuls les groupes qui ont des
 * bulles se montrent, toujours avec leur intertitre.
 *
 * Dans un groupe, c'est TON ordre qui ordonne, jamais un calcul : celui que le serveur rend avec
 * la feuille (`ordre`, son `ordreHierarchique` : ton rang, puis l'arrivée), lu tel quel
 * (chantier nettoyage-feuille, ticket 04).
 */

export type CleDIntertitre = Priorite | 'non_reglee' | 'hors_jeu';

export interface Intertitre<B> {
  cle: CleDIntertitre;
  nom: string;
  glose: string;
  bulles: B[];
}

type BulleDeListe = Pick<BulleEnrichie, 'id' | 'etat' | 'priorite'>;

const ORDRE: CleDIntertitre[] = [...CHOIX_DE_PRIORITE,'non_reglee', 'hors_jeu'];

/** Le groupe d'une bulle : plus à faire, son importance, ou rien de réglé. */
export function cleDIntertitre(bulle: BulleDeListe): CleDIntertitre {
  if (bulle.etat === 'fait' || bulle.etat === 'sans_objet') return 'hors_jeu';
  return bulle.priorite?.retenue ?? 'non_reglee';
}

function nomEtGlose(cle: CleDIntertitre): { nom: string; glose: string } {
  if (cle === 'non_reglee') return { nom: IMPORTANCE_NON_REGLEE, glose: GLOSE_NON_REGLEE };
  if (cle === 'hors_jeu') return { nom: HORS_JEU, glose: GLOSE_HORS_JEU };
  return { nom: MOTS_PRIORITE[cle], glose: GLOSE_DE_PRIORITE[cle] };
}

export function intertitresDeLaListe<B extends BulleDeListe>(
  enfants: readonly B[],
  ordre: readonly string[]
): Intertitre<B>[] {
  const parMonOrdre = selonMonOrdre(ordre);
  return ORDRE.map((cle) => {
    const bulles = enfants.filter((e) => cleDIntertitre(e) === cle).sort(parMonOrdre);
    return { cle, ...nomEtGlose(cle), bulles };
  }).filter((groupe) => groupe.bulles.length > 0);
}
